<template>
  <div class="sp-tab" :class="classModifiers" part="base" role="none">
    <sp-button ref="button" variant="text" color="primary" part="activator">
      <span class="sp-tab__label">
        <span class="sp-tab__text"><slot /></span>
        <span class="sp-tab__label-reserve" aria-hidden="true">{{ label }}</span>
      </span>
    </sp-button>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver } from "@vueuse/core";
import { computed, onMounted, shallowRef, useTemplateRef, watch } from "vue";

import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["update-active"]);

const props = defineProps({
  panel: {
    type: String,
    required: true,
  },
  active: booleanAttribute(),
});

const host = useHostElement();
const label = shallowRef("");
const navigationActive = shallowRef(null);
const variant = shallowRef("default");
const size = shallowRef("medium");
const isActive = computed(() => navigationActive.value ?? toBoolean(props.active));
const tabindex = shallowRef(-1);
let panelElement = null;
const button = useTemplateRef("button");
const supportsElementReflection = "ariaControlsElements" in Element.prototype;

function syncTabControl() {
  const control = button.value?.controlElement;
  if (!control) {
    return;
  }

  control.setAttribute("role", "tab");
  control.setAttribute("aria-selected", String(isActive.value));
  control.tabIndex = tabindex.value;

  if (supportsElementReflection) {
    control.ariaControlsElements = panelElement ? [panelElement] : null;
  }
}

function syncLabel() {
  label.value = host?.textContent ?? "";
}

onMounted(syncLabel);
useMutationObserver(host, syncLabel, { childList: true, subtree: true, characterData: true });
watch([button, isActive, tabindex], syncTabControl, { immediate: true, flush: "post" });
useEventListener(button, "sp:ce:ready", syncTabControl);

const { exposeMethods } = useExpose();
exposeMethods({
  focus: (options) => button.value?.focus(options),
  setNavigationState(state) {
    navigationActive.value = state.active;
    tabindex.value = state.tabindex;
    panelElement = state.panel;
    variant.value = state.variant ?? "default";
    size.value = state.size ?? "medium";
    syncTabControl();
  },
});

const classModifiers = computed(() => ({
  "--is-active": isActive.value,
  [`--variant-${variant.value}`]: true,
  [`--size-${size.value}`]: true,
}));

watch(isActive, (value) => {
  emit("update-active", value);
});
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-tab {
  --sp-comp-button-border-radius: var(--sp-comp-tab-button-border-radius, 0);
  --sp-comp-button-text-color: var(--sp-comp-tab-button-text-color, #aaa);
  --sp-comp-button-default-hover-text-color: var(--sp-sys-color-primary);

  &__label {
    display: contents;
  }

  &__label-reserve {
    display: none;
  }

  /* An unset selected-state token leaves the sp-button value it names unset. */
  &.--variant-default {
    --sp-comp-button-primary-variant-text-text-color: var(--sp-comp-tab-button-text-color);
    --sp-comp-button-primary-hover-outlined-bg-color: var(--sp-comp-tab-hover-bg-color);
    --sp-comp-button-padding: var(--sp-comp-tab-button-padding);
    --sp-comp-button-min-width: var(--sp-comp-tab-button-min-width);

    /* The hidden bold copy of the label keeps the tab as wide as when it is selected. */
    .sp-tab__label {
      display: inline-grid;
      justify-items: start;
    }

    .sp-tab__text,
    .sp-tab__label-reserve {
      grid-area: 1 / 1;
    }

    .sp-tab__label-reserve {
      display: block;
      font-weight: var(--sp-comp-tab-active-font-weight, inherit);
      visibility: hidden;
      user-select: none;
    }

    &.--is-active {
      --sp-comp-button-primary-variant-text-text-color: var(--sp-comp-tab-active-text-color);
      --sp-comp-button-font-weight: var(--sp-comp-tab-active-font-weight);

      box-shadow: inset 0 calc(-1 * var(--sp-comp-tab-indicator-height, 0px)) 0
        var(--sp-comp-tab-active-indicator-color, transparent);
    }
  }

  &.--variant-segmented {
    --segmented-text-color: var(--sp-comp-tab-segmented-text-color, var(--sp-ref-palette-neutral-40));
    --segmented-hover-text-color: var(--sp-comp-tab-segmented-hover-text-color, var(--sp-sys-color-on-surface));
    --segmented-active-text-color: var(--sp-comp-tab-segmented-active-text-color, var(--sp-sys-color-on-surface));
    --segmented-active-background-color: var(
      --sp-comp-tab-segmented-active-background-color,
      var(--sp-sys-color-surface)
    );
    --segmented-active-box-shadow: var(--sp-comp-tab-segmented-active-box-shadow, var(--sp-sys-elevation-4));
    --segmented-border-radius: var(--sp-comp-tab-segmented-border-radius, var(--sp-ref-border-radius-full));
    --sp-comp-button-border-radius: var(--segmented-border-radius);
    --sp-comp-button-focus-border-radius: var(--segmented-border-radius);
    --sp-comp-button-size: var(--sp-comp-tab-segmented-height, var(--sp-ref-scale-32));
    --sp-comp-button-padding: var(--sp-ref-scale-6) var(--sp-ref-scale-12);
    --sp-comp-button-min-width: var(--sp-ref-scale-64);
    --sp-comp-button-width: 100%;
    --sp-comp-button-font-family: var(--sp-sys-font-family-normal);
    --sp-comp-button-font-weight: var(--sp-sys-font-weight-normal);
    --sp-comp-button-font-size: var(--sp-ref-font-size-semi-medium);
    --sp-comp-button-line-height: var(--sp-ref-line-height-ts-small-body);
    --sp-comp-button-primary-variant-text-text-color: var(--segmented-text-color);
    --sp-comp-button-primary-hover-variant-text-text-color: var(--segmented-hover-text-color);
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0;
    --sp-comp-button-focus-outline: none;
    --sp-comp-button-focus-visible-outline: var(--sp-sys-state-focus-indicator-thickness) solid
      var(--sp-sys-state-focus-indicator-color);
    --sp-comp-button-focus-visible-outline-offset: var(--sp-sys-state-focus-indicator-outer-offset);
    --sp-comp-button-focus-visible-opacity: 1;

    border-radius: var(--segmented-border-radius);

    &.--size-small {
      --sp-comp-button-size: var(--sp-comp-tab-segmented-small-height, var(--sp-ref-scale-28));
      --sp-comp-button-line-height: var(--sp-ref-line-height-ts-footnote);
    }

    &.--is-active {
      --sp-comp-button-primary-variant-text-text-color: var(--segmented-active-text-color);
      --sp-comp-button-primary-hover-variant-text-text-color: var(--segmented-active-text-color);

      background: var(--segmented-active-background-color);
      box-shadow: var(--segmented-active-box-shadow);
    }
  }
}
</style>
